import React, { useState, useEffect } from 'react';
import {
  LeftSidePopupsForAdding,
  Portfolio,
  ReturnedPortfolio,
  ReturnedReference,
} from '../Create-Portfolio/types';
import { Button, Card, ButtonGroup, Alert, Modal } from 'react-bootstrap';
import { ReactComponent as PlusIcon } from '../../images/plus.svg';
import { ReactComponent as PrevIcon } from '../../images/arrow-prev.svg';
import { ReactComponent as NextIcon } from '../../images/arrow-next.svg';
import { ReactComponent as EditIcon } from '../../images/edit.svg';
import { ReactComponent as DeleteIcon } from '../../images/trash3-fill.svg';
import { isReferenceFilled } from '../Create-Portfolio/helpers';
import { workCultureArray } from '../../config/work-culture-components';
import { levels } from '../../config/languages';
import { getLanguageLevelClassName } from '../../utils/languageDisplay';
import ProfileLanguageDisplay from '../UserProfile/ProfileLanguageDisplay';
import ReferencePopup from '../Popups/ReferencePopup';
import WorkCulturePopup from '../Popups/WorkCulturePopup';
import LanguagePopup from '../Popups/LanguagePopup';
import './Preview.css';
import '../UserProfile/ProfilePortfolioSections.css';
import RemoveModal from '../RemoveModal';
import axiosInstance from '../../api/api';
import { API_ROUTES } from '../../config/api-routes';
import {
  referenceResendError,
  resendReferenceVerificationEmail,
} from '../../utils/referenceResend';
import {
  getReferenceFeedbackSummary,
  parseReferenceVerificationAnswers,
} from '../../utils/referenceFeedback';
import { Link, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  REFERENCE_AWAITING_VERIFICATION,
  REFERENCE_HELPER_TEXT,
  WORK_CULTURE_EMPTY_HELPER,
} from '../../utils/portfolioSectionMessages';

interface PortfolioCardsProps {
  portfolio: Portfolio | ReturnedPortfolio;
  userProfile?: string;
  /** sidebar = work culture + languages; references = references only; all = everything */
  sections?: 'all' | 'sidebar' | 'references';
  setPortfolioData?: React.Dispatch<React.SetStateAction<ReturnedPortfolio>>;
  setSavePortfolioButton?: React.Dispatch<React.SetStateAction<boolean>>;
}

const PortfolioCards: React.FC<PortfolioCardsProps> = ({
  portfolio,
  userProfile,
  sections = 'all',
  setPortfolioData,
  setSavePortfolioButton,
}) => {
  const location = useLocation();
  const referenceDetailState = {
    from: `${location.pathname}${location.search}`,
  };
  const showReferences = sections === 'all' || sections === 'references';
  const showSidebarSections = sections === 'all' || sections === 'sidebar';
  const {
    references,
    languages,
    workCulture,
    isReferencesEnabled,
    isLanguagesEnabled,
    isWorkCultureEnabled,
  } = portfolio;

  const [currentReferenceIndex, setCurrentReferenceIndex] = useState<number>(0);
  const [customOptions, setCustomOptions] = useState<string[]>([]);
  const [showRemoveModal, setShowRemoveModal] = useState<boolean>(false);

  const [openedPopupsToAdd, setOpenedPopupsToAdd] = useState<LeftSidePopupsForAdding>({
    references: false,
    languages: false,
    workCulture: false,
  });
  const [openedPopupsToEdit, setOpenedPopupsToEdit] = useState<{
    references: boolean[];
  }>({
    references: portfolio?.references.map(() => false),
  });
  const [message, setMessage] = useState<string>('');
  const [errorMessage, setErrorMessage] = useState<string>('');

  const togglePopups = (popupName: 'references', index: number) => {
    setOpenedPopupsToEdit((prev) => {
      const popupArray = prev[popupName];
      if (Array.isArray(popupArray)) {
        const newPopupArray = [...popupArray];
        newPopupArray[index] = !newPopupArray[index];
        return {
          ...prev,
          [popupName]: newPopupArray,
        };
      }
      return prev;
    });
  };

  const handleTogglePopupsForAdding = (field: keyof LeftSidePopupsForAdding) => {
    setOpenedPopupsToAdd((prev) => ({ ...prev, [field]: !prev[field] }));
  };

  const handlePrev = () => {
    setCurrentReferenceIndex((prevIndex) =>
      prevIndex > 0 ? prevIndex - 1 : references.length - 1
    );
  };

  const handleNext = () => {
    setCurrentReferenceIndex((prevIndex) =>
      prevIndex < references.length - 1 ? prevIndex + 1 : 0
    );
  };

  const filteredWorkCulture = workCulture?.filter(
    (item) => workCultureArray.includes(item) && item !== 'Other (Custom)'
  );

  useEffect(() => {
    const customWorkCulture = workCulture?.filter((item) => !workCultureArray.includes(item));
    if (customWorkCulture) {
      setCustomOptions(customWorkCulture);
    }
  }, [workCulture]);

  const handleOpenRemoveModal = () => setShowRemoveModal(true);
  const handleCloseRemoveModal = () => setShowRemoveModal(false);

  const activeReference = references?.[currentReferenceIndex] as ReturnedReference | undefined;
  const canEditActiveReference = Boolean(activeReference && !activeReference.isReferenceConfirmed);

  const handleRemoveReference = async () => {
    try {
      const referenceId = references[currentReferenceIndex].id;
      const response = await axiosInstance.delete(
        `${API_ROUTES.REFERENCE.UPDATE_REFERENCE}/${referenceId}`
      );
      if (response.status === 200) {
        const updatedReferences = references.filter((_, index) => index !== currentReferenceIndex);
        setPortfolioData?.((prev) => ({
          ...prev,
          references: updatedReferences,
        }));
        setCurrentReferenceIndex((prev) => (prev > 0 ? prev - 1 : 0));
        setSavePortfolioButton?.(true);
        setOpenedPopupsToEdit((prev) => ({
          ...prev,
          references: prev.references.map((_, i) => (i === currentReferenceIndex ? false : _)),
        }));
        toast.success('Reference deleted successfully');
      } else {
        console.error('Failed to delete reference');
      }
    } catch (error) {
      console.error('Error deleting reference:', error);
    }
    setShowRemoveModal(false);
  };

  const handleResendConfirmationEmail = async () => {
    const referenceId = references[currentReferenceIndex]?.id;
    if (!referenceId) {
      toast.error('We could not identify this reference. Please refresh and try again.');
      return;
    }
    try {
      const detail = await resendReferenceVerificationEmail(referenceId);
      setMessage(detail);
      toast.success(detail);
    } catch (error) {
      const message = referenceResendError(error);
      setErrorMessage(message);
      toast.error(message);
    }
  };

  return (
    <>
      {showReferences && isReferencesEnabled && (
        <Card
          id="references-card"
          className="references-card-modern"
          data-profile-focus="references"
        >
          <Card.Header className="preview-cards-ref-header references-header">
            <span className="references-title">References</span>
            <div className="references-header-actions">
              {userProfile === 'profile' ? (
                <button
                  type="button"
                  title="Add Reference"
                  className="user-info-edit-button"
                  style={{ position: 'relative' }}
                  data-profile-focus="references-add"
                  onClick={() => handleTogglePopupsForAdding('references')}
                >
                  <PlusIcon
                    style={{
                      width: '20px',
                      height: '20px',
                    }}
                  />
                </button>
              ) : (
                <PlusIcon
                  style={{
                    width: '20px',
                    height: '20px',
                  }}
                />
              )}

              {openedPopupsToAdd.references && (
                <Modal
                  className="ss-app-modal"
                  show={openedPopupsToAdd.references} // Control modal visibility
                  centered
                  scrollable
                  keyboard={false}
                  onBackdropClick={() =>
                    setOpenedPopupsToAdd((prev) => ({
                      ...prev,
                      references: false,
                    }))
                  }
                  onEscapeKeyDown={() =>
                    setOpenedPopupsToAdd((prev) => ({
                      ...prev,
                      references: true,
                    }))
                  }
                  onHide={() =>
                    setOpenedPopupsToAdd((prev) => ({
                      ...prev,
                      references: false,
                    }))
                  }
                >
                  <Modal.Header closeButton>
                    <Modal.Title>Add References</Modal.Title>
                  </Modal.Header>
                  <Modal.Body style={{ width: '100%' }}>
                    <ReferencePopup
                      setPopupOpen={setOpenedPopupsToAdd}
                      setPortfolioData={setPortfolioData}
                      setSavePortfolioButton={setSavePortfolioButton}
                    />
                    {/* <Card className="user-left-popup p-3">
                      <Card.Body className="scrollable-card">
                        <Card.Title className="mb-3">
                          Edit / Add reference
                        </Card.Title>
                        <Form>
                          <Form.Group as={Row} className="mb-3">
                            <Col sm={12}>
                              <Form.Control
                                type="text"
                                required
                                placeholder="Enter a name"
                              />
                            </Col>
                          </Form.Group>
                          <Form.Group as={Row} className="mb-3">
                            <Col sm={12}>
                              <Form.Control
                                type="email"
                                required
                                placeholder="Enter a email"
                              />
                            </Col>
                          </Form.Group>
                          <Form.Group as={Row} className="mb-3">
                            <Col sm={12}>
                              <Form.Control
                                type="text"
                                required
                                placeholder="Enter a role"
                              />
                            </Col>
                          </Form.Group>
                          <Form.Group as={Row} className="mb-3">
                            <Col sm={12}>
                              <Form.Control
                                type="text"
                                required
                                placeholder="Enter an organization"
                              />
                            </Col>
                          </Form.Group>
                          <Button variant="success" type="submit">
                            Submit
                          </Button>
                        </Form>
                      </Card.Body>
                    </Card> */}
                  </Modal.Body>
                </Modal>
              )}
            </div>
          </Card.Header>
          <Card.Body className="references-body">
            <p className="references-v2-helper small text-muted mb-3">{REFERENCE_HELPER_TEXT}</p>
            <div className="references-toolbar">
              <span></span>
              <div className="references-toolbar-row">
                {references?.length ? (
                  <div className="references-meta">
                    <div className="references-count">
                      <span>{currentReferenceIndex + 1}</span>
                      <span>{` of ${references.length}`}</span>
                    </div>
                    <div className="text-muted small mt-1 references-subtitle">
                      {references[currentReferenceIndex]?.name || 'Reference'}
                      {references[currentReferenceIndex]?.email
                        ? ` • ${references[currentReferenceIndex]?.email}`
                        : ''}
                    </div>
                    {(() => {
                      const ref = references[currentReferenceIndex] as ReturnedReference;
                      if (!ref?.id) return null;
                      return (
                        <Link
                          to={`/dashboard/references/${ref.id}`}
                          state={referenceDetailState}
                          className="reference-view-details-inline"
                        >
                          View details →
                        </Link>
                      );
                    })()}
                  </div>
                ) : (
                  <div className="references-empty">0 of 0</div>
                )}
                <div className="references-actions">
                  {references?.length ? (
                    <Button
                      variant="outline-danger"
                      size="sm"
                      onClick={handleOpenRemoveModal}
                      title="Delete Reference"
                      className="references-delete"
                    >
                      <DeleteIcon width={14} height={14} />
                    </Button>
                  ) : null}
                  {references?.length && canEditActiveReference ? (
                    <button type="button" className="user-info-edit-button">
                      <EditIcon
                        style={{ width: '15px', height: '15px' }}
                        onClick={() => togglePopups('references', currentReferenceIndex)}
                      />
                      {openedPopupsToEdit.references[currentReferenceIndex] && (
                        <Modal
                          className="ss-app-modal"
                          scrollable
                          show={openedPopupsToEdit.references[currentReferenceIndex]} // Control modal visibility
                          centered
                          keyboard={false}
                          onBackdropClick={() =>
                            setOpenedPopupsToEdit((prev) => ({
                              ...prev,
                              references: prev.references.map((_, i) =>
                                i === currentReferenceIndex ? false : _
                              ),
                            }))
                          }
                          onEscapeKeyDown={() =>
                            setOpenedPopupsToEdit((prev) => ({
                              ...prev,
                              references: prev.references.map((_, i) =>
                                i === currentReferenceIndex ? false : _
                              ),
                            }))
                          }
                          onHide={() =>
                            setOpenedPopupsToEdit((prev) => ({
                              ...prev,
                              references: prev.references.map((_, i) =>
                                i === currentReferenceIndex ? false : _
                              ),
                            }))
                          }
                        >
                          <Modal.Header closeButton>
                            <Modal.Title>Edit References</Modal.Title>
                          </Modal.Header>
                          <Modal.Body style={{ width: '100%' }}>
                            <ReferencePopup
                              initialReference={references[currentReferenceIndex]}
                              setOpenedPopups={setOpenedPopupsToEdit}
                              setPortfolioData={setPortfolioData}
                              setSavePortfolioButton={setSavePortfolioButton}
                              index={currentReferenceIndex}
                            />
                          </Modal.Body>
                        </Modal>
                      )}
                    </button>
                  ) : null}

                  {references?.length ? (
                    <ButtonGroup size="sm" className="references-nav">
                      <Button
                        variant="outline-secondary"
                        onClick={handlePrev}
                        disabled={!references?.length}
                      >
                        <PrevIcon style={{ width: '15px', height: '15px' }} />
                      </Button>
                      <Button
                        variant="outline-secondary"
                        onClick={handleNext}
                        disabled={!references?.length}
                      >
                        <NextIcon style={{ width: '15px', height: '15px' }} />
                      </Button>
                    </ButtonGroup>
                  ) : null}
                </div>
                <RemoveModal
                  showModal={showRemoveModal}
                  onCloseModal={handleCloseRemoveModal}
                  onSubmit={handleRemoveReference}
                />
              </div>
            </div>
            {references?.length > 0 && references.every(isReferenceFilled) && (
              <div className="references-content-wrapper">
                <div className="preview-ref-content references-content">
                  {userProfile === 'profile' &&
                  references[currentReferenceIndex] &&
                  !(references[currentReferenceIndex] as ReturnedReference).isReferenceConfirmed ? (
                    <>
                      <Alert variant="warning" className="references-alert">
                        {REFERENCE_AWAITING_VERIFICATION}
                      </Alert>
                      <Button
                        variant="success"
                        className="references-resend"
                        onClick={handleResendConfirmationEmail}
                      >
                        Resend Confirmation Email
                      </Button>
                      {message && (
                        <Alert variant="success" className="references-alert">
                          {message}
                        </Alert>
                      )}
                      {errorMessage && (
                        <Alert variant="danger" className="references-alert">
                          {errorMessage}
                        </Alert>
                      )}
                    </>
                  ) : (
                    <div className="references-details">
                      <h5>{references[currentReferenceIndex]?.name}</h5>
                      <p>{references[currentReferenceIndex]?.role}</p>
                      <p>{references[currentReferenceIndex]?.organization}</p>
                      <p>{references[currentReferenceIndex]?.email}</p>
                      {(() => {
                        const ref = references[currentReferenceIndex] as ReturnedReference;
                        const answers = parseReferenceVerificationAnswers(
                          ref?.verificationAnswers ??
                            (ref as { verification_answers?: unknown }).verification_answers
                        );
                        const summary = getReferenceFeedbackSummary(answers);
                        if (!ref?.id) return null;
                        return (
                          <>
                            {summary ? (
                              <p className="reference-feedback-summary">{summary}</p>
                            ) : null}
                            <Link
                              to={`/dashboard/references/${ref.id}`}
                              state={referenceDetailState}
                              className="reference-view-response-btn"
                            >
                              📝 View what {ref.name?.split(' ')[0] || 'they'} said
                            </Link>
                          </>
                        );
                      })()}
                    </div>
                  )}
                </div>
              </div>
            )}
          </Card.Body>
        </Card>
      )}
      {showSidebarSections && isWorkCultureEnabled && (
        <Card className="profile-work-culture-card" data-profile-focus="work-culture">
          <Card.Header className="preview-cards-ref-header">
            <span>My Ideal Work Culture</span>
            <div>
              {userProfile === 'profile' ? (
                <button
                  type="button"
                  title="edit"
                  className="user-info-edit-button"
                  data-profile-focus="work-culture-edit"
                  onClick={() => handleTogglePopupsForAdding('workCulture')}
                >
                  <EditIcon style={{ width: '15px', height: '15px' }} />
                </button>
              ) : (
                <EditIcon style={{ width: '15px', height: '15px' }} />
              )}

              {openedPopupsToAdd.workCulture && (
                <Modal
                  className="ss-app-modal"
                  show={openedPopupsToAdd.workCulture} // Control modal visibility
                  scrollable
                  onHide={() =>
                    setOpenedPopupsToAdd((prev) => ({
                      ...prev,
                      workCulture: false,
                    }))
                  }
                  centered
                >
                  <Modal.Header closeButton>
                    <Modal.Title>Edit Work Culture</Modal.Title>
                  </Modal.Header>
                  <Modal.Body style={{ width: '100%' }}>
                    <WorkCulturePopup
                      setPopupOpen={setOpenedPopupsToAdd}
                      setPortfolioData={setPortfolioData}
                      setSavePortfolioButton={setSavePortfolioButton}
                      workCulture={workCulture}
                    />
                  </Modal.Body>
                </Modal>
              )}
            </div>
          </Card.Header>
          <Card.Body>
            {userProfile === 'profile' && !filteredWorkCulture?.length && !customOptions.length ? (
              <p className="profile-section-empty mb-0">{WORK_CULTURE_EMPTY_HELPER}</p>
            ) : (
              <>
                <div className="preview-work-wrapper mb-3">
                  {filteredWorkCulture?.map((item, index) => (
                    <span key={index} className="preview-spans preview-span-green">
                      {item}
                    </span>
                  ))}
                </div>
                {customOptions.map((customOption, index) => (
                  <div key={index} className="preview-work-wrapper mb-3">
                    <span className="preview-spans preview-span-green">{customOption}</span>
                  </div>
                ))}
              </>
            )}
          </Card.Body>
        </Card>
      )}
      {showSidebarSections && isLanguagesEnabled && (
        <Card className="profile-languages-card" data-profile-focus="languages">
          <Card.Header className="preview-cards-ref-header">
            <span>Languages</span>
            {userProfile === 'profile' ? (
              <button
                type="button"
                title="Add Language"
                className="user-info-edit-button"
                data-profile-focus="languages-add"
                onClick={() => handleTogglePopupsForAdding('languages')}
              >
                <PlusIcon style={{ width: '20px', height: '20px' }} />
              </button>
            ) : (
              <PlusIcon style={{ width: '20px', height: '20px' }} />
            )}
            {openedPopupsToAdd.languages && (
              <Modal
                className="ss-app-modal languages-manage-modal"
                show={openedPopupsToAdd.languages}
                scrollable
                onHide={() =>
                  setOpenedPopupsToAdd((prev) => ({
                    ...prev,
                    languages: false,
                  }))
                }
                centered
                size="lg"
              >
                <LanguagePopup
                  setPopupOpen={setOpenedPopupsToAdd}
                  languages={languages}
                  setPortfolioData={setPortfolioData}
                  setSavePortfolioButton={setSavePortfolioButton}
                />
              </Modal>
            )}
          </Card.Header>
          <Card.Body>
            {userProfile === 'profile' ? (
              <>
                <ProfileLanguageDisplay languages={languages} />
                {languages.length > 0 ? (
                  <div className="profile-languages-footer">
                    Languages are shown on your public profile to help recruiters find the right
                    fit.
                  </div>
                ) : null}
              </>
            ) : languages.length === 0 ? (
              <p className="portfolio-languages-empty mb-0">No languages listed.</p>
            ) : (
              <>
                <div className="preview-colors-wrapper">
                  {levels.map(({ level, color }) => (
                    <span key={level} style={{ display: 'flex', alignItems: 'center' }}>
                      <span style={{ color: color, marginRight: '8px' }}>⬤</span>
                      <span>{level}</span>
                    </span>
                  ))}
                </div>
                <div className="preview-work-wrapper">
                  {languages.map((item, index) => (
                    <span key={index} className={getLanguageLevelClassName(item.level)}>
                      {item.language}
                    </span>
                  ))}
                </div>
              </>
            )}
          </Card.Body>
        </Card>
      )}
    </>
  );
};

export default PortfolioCards;
